import React from 'react'
import { Routes, Route } from 'react-router-dom'
import { LandingPage } from './pages/LandingPage'
import { ExplorePage } from './pages/ExplorePage'
import { StoryGenerator } from './pages/StoryGenerator'
import { StoryEditor } from './pages/StoryEditor'
import { StoryPlayer } from './pages/StoryPlayer'
import { MyWorld } from './pages/MyWorld'
import { PlayCreateMode } from './pages/PlayCreateMode'
import { WorldSimulatorList } from './pages/WorldSimulatorList'
import { Workshop } from './pages/Workshop'
import { WorldExperience } from './pages/WorldExperience'
import { WorldCreator } from './pages/WorldCreator'
import { WorldProfile } from './pages/WorldProfile'
import { ContentDetail } from './pages/ContentDetail'
import { Layout } from './components/Layout'
import ErrorBoundary from './components/ErrorBoundary'
import { ServiceWorkerUpdatePrompt } from './components/ServiceWorkerUpdatePrompt'
import { CORSErrorHandler } from './components/CORSErrorHandler'
import { ThemeProvider } from './contexts/ThemeContext'
import { useStoryStore } from './stores/storyStore'
import AuthInitService from './services/authInitService'
import AppConfig from './utils/config'
import { PerformanceMonitor as PerformanceUtils } from './utils/performance'
import { SecurityUtils } from './utils/security'
import performanceMonitor from './services/performanceMonitor'
import memoryManager from './services/memoryManager'
import renderOptimizer from './services/renderOptimizer'
import networkOptimizer from './services/networkOptimizer'
import ServiceWorkerHelper from './utils/serviceWorkerHelper'

function App() {
  const { initializeStore } = useStoryStore()

  React.useEffect(() => {
    // 初始化应用
    initializeApp()
  }, [])

  const initializeApp = async () => {
    try {
      // 初始化认证系统
      await AuthInitService.init()
      
      // 初始化存储
      await initializeStore()
      
      // 初始化性能监控
      PerformanceUtils.init()
      
      // 初始化性能优化服务
      performanceMonitor.init()
      memoryManager.init()
      renderOptimizer.init()
      networkOptimizer.init()
      
      // 设置内容安全策略
      if (AppConfig.get('security.enableCSP')) {
        setContentSecurityPolicy()
      }
      
      // 检查存储配额
      const quota = await SecurityUtils.checkStorageQuota()
      if (quota.isNearLimit) {
        console.warn('⚠️ 存储空间接近限制:', quota)
      }
      
      console.log('🚀 应用初始化完成')
    } catch (error) {
      console.error('❌ 应用初始化失败:', error)
    }
  }

  const setContentSecurityPolicy = () => {
    const cspConfig = SecurityUtils.getCSPConfig()
    const cspString = Object.entries(cspConfig)
      .map(([directive, sources]) => `${directive} ${sources.join(' ')}`)
      .join('; ')
    
    // 创建meta标签设置CSP
    const meta = document.createElement('meta')
    meta.httpEquiv = 'Content-Security-Policy'
    meta.content = cspString
    document.head.appendChild(meta)
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Layout>
          <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/workshop" element={<ExplorePage />} />
          <Route path="/create" element={<StoryGenerator />} />
          <Route path="/my-games" element={<MyWorld />} />
          <Route path="/my-world" element={<MyWorld />} />
          {/* 内容详情页（统一的路由） */}
          <Route path="/content/:type/:id" element={<ContentDetail />} />
          {/* 玩创模式路由（保留用于创建和聊天） */}
          <Route path="/play-create" element={<PlayCreateMode />} />
          <Route path="/play-create/chat/:characterId" element={<PlayCreateMode />} />
          <Route path="/editor/:storyId" element={<StoryEditor />} />
          <Route path="/player/:storyId" element={<StoryPlayer />} />
          {/* 世界模拟器路由（保留用于体验和创建） */}
          <Route path="/world-simulator" element={<ExplorePage />} />
          <Route path="/world-simulator/experience/:worldId" element={<WorldExperience />} />
          <Route path="/world-simulator/create" element={<WorldCreator />} />
          <Route path="/world-simulator/profile" element={<MyWorld />} />
          </Routes>
        </Layout>
        <ServiceWorkerUpdatePrompt />
        <CORSErrorHandler />
      </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App
